import { useEffect, Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import GlobalStyles from "./styles/globalStyles";
import ProtectedRoute from "./components/ProtectedRoute/ProtectedRoute";
import ScrollIndicator from "./components/utils/ScrollIndicator";

import { ACCESS } from "./utils/constants";
import {
  publicRoutes,
  authRoutes,
  userRoutes,
  adminRoutes,
} from "./config/routeConfig";

import { refresh } from "./store/slices/auth/asyncThunk";
import { getInfoAboutAdmin } from "./store/slices/admin/asyncThunk";
import { selectLoggedIn, setLoggedIn } from "./store/slices/auth/slice";

import { Toaster } from "sonner";
import "sonner/dist/styles.css";
import { CartProvider } from "./store/CartContext";

// Stranice koje nisu u config-u
import EmailVerificationNotice from "./pages/Auth/EmailVerificationNotice";
import Checkout from "./pages/Checkout/Checkout";
import PaymentSuccess from "./pages/PaymentSuccess/PaymentSuccess";
import PaymentFailure from "./pages/PaymentFailure/PaymentFailure";
import LoadingScreen from "./components/utils/LoadingScreen";
import { lineSpinner, ring2 } from "ldrs";

lineSpinner.register();
ring2.register();

function App() {
  const dispatch = useDispatch();
  const loggedIn = useSelector(selectLoggedIn);

  useEffect(() => {
    // Check if we need to refresh token on app load
    const token =
      localStorage.getItem("accessToken") ||
      sessionStorage.getItem("accessToken");
    if (token && !loggedIn) {
      dispatch(refresh())
        .unwrap()
        .then(() => dispatch(getInfoAboutAdmin()))
        .catch(() => dispatch(setLoggedIn({ loggedIn: false })));
    }
    // eslint-disable-next-line
  }, [dispatch]); // Remove loggedIn dependency to avoid re-render loop

  // Helper za renderovanje protected ruta
  const renderProtectedRoute = ({ path, element, access }) => (
    <Route
      key={path}
      path={path}
      element={
        <ProtectedRoute
          access={access}
          allowEmailVerification={path === "/verifikacija-email"}
        >
          {element}
        </ProtectedRoute>
      }
    />
  );

  return (
    <CartProvider>
      <GlobalStyles />
      <ScrollIndicator />

      <Suspense fallback={<LoadingScreen />}>
        <Routes>
          {/* Javne rute */}
          {publicRoutes.map(({ path, element }) => (
            <Route key={path} path={path} element={element} />
          ))}

          {/* Auth rute (redirect ako ulogovan) */}
          {authRoutes.map(renderProtectedRoute)}

          {/* Posebne rute */}
          <Route
            path="/verifikacija-potrebna"
            element={<EmailVerificationNotice />}
          />

          <Route
            path="/checkout"
            element={
              <ProtectedRoute access={ACCESS.VERIFIED}>
                <Checkout />
              </ProtectedRoute>
            }
          />

          {/* Payment result pages */}
          <Route path="/placanje-uspesno" element={<PaymentSuccess />} />
          <Route path="/placanje-neuspesno" element={<PaymentFailure />} />

          {/* User rute */}
          {userRoutes.map(renderProtectedRoute)}

          {/* Admin rute */}
          {adminRoutes.map(renderProtectedRoute)}

          {/* Fallback: nepoznata ruta → početna (izbegava prazan ekran) */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>

      <Toaster position="bottom-center" closeButton={false} />
    </CartProvider>
  );
}

export default App;
